import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ExternalLink, Copy } from "lucide-react";
import { formatAddress } from "@/utils/format";
import { useWalletState } from "@/hooks/useWalletState";
import { toast } from "sonner";

interface LiquidityConfirmationProps {
  lpAddress: string;
  tokenAddress: string;
  tokenSymbol: string;
  bnbAmount: string;
  tokenAmount: string;
}

export function LiquidityConfirmation({ 
  lpAddress, 
  tokenAddress, 
  tokenSymbol, 
  bnbAmount, 
  tokenAmount 
}: LiquidityConfirmationProps) {
  const { chainId } = useWalletState();
  
  // Get network specific details
  const getNetworkDetails = () => {
    switch (chainId) {
      case 97: // BSC Testnet
        return {
          explorer: `https://testnet.bscscan.com/token/${lpAddress}`,
          currency: 'BNB',
          explorerName: 'BSCScan'
        };
      case 56: // BSC Mainnet
        return {
          explorer: `https://bscscan.com/token/${lpAddress}`,
          currency: 'BNB',
          explorerName: 'BSCScan'
        };
      case 11155111: // Sepolia
        return {
          explorer: `https://sepolia.etherscan.io/token/${lpAddress}`,
          currency: 'ETH',
          explorerName: 'Etherscan'
        };
      case 1: // Ethereum Mainnet
        return {
          explorer: `https://etherscan.io/token/${lpAddress}`,
          currency: 'ETH',
          explorerName: 'Etherscan'
        };
      default:
        return {
          explorer: '',
          currency: 'ETH',
          explorerName: 'Explorer'
        };
    }
  };

  const { explorer, currency, explorerName } = getNetworkDetails();

  const copyAddress = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      await navigator.clipboard.writeText(lpAddress);
      toast.success("LP address copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy address");
    }
  };

  const addToWallet = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      if (!window.ethereum) {
        throw new Error("No crypto wallet found");
      }

      // Get the correct token type, name, and symbol based on network
      const isEthereumNetwork = chainId === 1 || chainId === 11155111;
      
      // Use Cake-LP for PancakeSwap and UNI-V2 for Uniswap
      const lpSymbol = isEthereumNetwork ? 'UNI-V2' : 'Cake-LP';
      const name = isEthereumNetwork
        ? `Uniswap V2 ${tokenSymbol}-ETH LP`
        : `PancakeSwap V2 ${tokenSymbol}-BNB LP`;

      await window.ethereum.request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC20',
          options: {
            address: lpAddress,
            symbol: lpSymbol,
            decimals: 18,
            name: name,
            image: undefined // Optional token logo URL
          }
        }
      });
      
      toast.success("LP token added to wallet!");
    } catch (error: any) {
      console.error("Add to wallet error:", error);
      const errorMessage = error.message || "Failed to add token to wallet";
      const cleanError = errorMessage.replace("MetaMask:", "").trim();
      toast.error(cleanError);
    }
  };

  return (
    <Card className="border-2 border-[#0a74ff] dark:border-[#0a74ff] bg-gradient-to-r from-white via-[#0a74ff]/5 to-white dark:from-black dark:via-[#0a74ff]/10 dark:to-black">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#0a74ff] via-[#0a74ff]/90 to-[#0a74ff]/80">
            Liquidity Added Successfully! 🎉
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-4">
          {/* LP Address */}
          <div className="space-y-1.5">
            <label className="text-sm text-muted-foreground">LP Token Address</label>
            <div className="flex items-center gap-2 bg-background/50 p-2 rounded-md border border-[#0a74ff]/20">
              <code className="flex-1 text-sm">{formatAddress(lpAddress)}</code>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={copyAddress}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Liquidity Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-muted-foreground">{currency} Amount</label>
              <p className="text-lg font-medium">{bnbAmount} {currency}</p>
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Token Amount</label>
              <p className="text-lg font-medium">{tokenAmount} {tokenSymbol}</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-4 pt-4">
            <Button 
              variant="outline" 
              className="w-full border-[#0a74ff]/50 hover:bg-[#0a74ff]/10"
              onClick={() => window.open(explorer, '_blank')}
              type="button"
            >
              View on {explorerName}
              <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
            <Button 
              className="w-full bg-[#0a74ff] hover:bg-[#0a74ff]/90"
              onClick={addToWallet}
              type="button"
            >
              Add LP to Wallet
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
